/**
 * useTradePack + helpers
 *
 * Hooks for accessing the active trade pack. Layered ON `useTenant()`:
 *
 *   useTenant() → tenant.tradePackId → useTradePack(packId)
 *               → cached Firestore subscription on trade_packs/{packId}
 *
 * Loading-state contract: NEVER renders a spinner. During the brief window
 * between mount and Firestore resolving the pack doc, hooks return data
 * derived from the static SERVICE_CONFIGS fallback in service-configs.ts.
 * When the pack subscription resolves, hooks swap to pack-driven data with
 * no layout shift.
 *
 * @see docs/TRADE_PACKS.md - Pack schema + invariants
 * @see CLAUDE.md - Architecture Standards (tier/pack model)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { useTenant } from '@/hooks/useTenant';
import {
  TradePack,
  ServiceCategoryDef,
  ServiceRequestTypeDef,
  ComplianceFlow,
  DEFAULT_TRADE_PACK_ID,
} from '@/types/trade-pack.types';
import { SERVICE_CONFIGS, type ServiceConfig } from '@/data/service-configs';
import { humanizeRequestTypeKey } from '@shared/domain/service-request-types';
import {
  SERVICE_REQUEST_TYPE_LABELS,
  SERVICE_REQUEST_TYPE_DESCRIPTIONS,
  type ServiceRequestType,
} from '@/types/service-request.types';

// ---------------------------------------------------------------------------
// Synchronous static fallback
//
// Convert today's hardcoded SERVICE_CONFIGS map into ServiceCategoryDef[] so
// the same downstream consumers can render before the pack subscription
// resolves. This is the "telco-installer" pack expressed as code.
// ---------------------------------------------------------------------------

function serviceConfigToCategoryDef(config: ServiceConfig): ServiceCategoryDef {
  return {
    key: String(config.category),
    label: config.label,
    icon: config.icon,
    color: config.color,
    intakeFieldDefs: config.intakeFieldDefs,
    scopeFieldDefs: config.presiteFieldDefs,
    completionFieldDefs: config.postQuoteFieldDefs,
  };
}

const STATIC_FALLBACK_CATEGORIES: ServiceCategoryDef[] = Object.values(
  SERVICE_CONFIGS,
).map(serviceConfigToCategoryDef);

const STATIC_FALLBACK_PACK: TradePack = {
  id: DEFAULT_TRADE_PACK_ID,
  name: 'Telco Installer',
  appliesTo: ['lite', 'pro'],
  serviceCategories: STATIC_FALLBACK_CATEGORIES,
  complianceFlows: [],
  terminology: {},
  routes: { enabled: [], hidden: [] },
  reportTemplates: [],
  dashboardWidgets: [],
};

// ---------------------------------------------------------------------------
// useTradePack — primary subscription
// ---------------------------------------------------------------------------

export interface UseTradePackResult {
  /** The active pack. Always non-null — falls back to STATIC_FALLBACK_PACK
   *  during loading or when the pack doc doesn't exist. */
  pack: TradePack;
  /** True only while the Firestore subscription is resolving for the first
   *  time. Most consumers should NOT branch on this — the synchronous
   *  fallback makes the data usable immediately. */
  isLoading: boolean;
  /** Firestore error if the subscription failed. Pack continues to fall back
   *  to the static default. */
  error: string | null;
  /** True when the pack came from the live Firestore subscription rather
   *  than the synchronous static fallback. Useful for diagnostics; consumers
   *  should usually ignore this. */
  isLive: boolean;
}

/**
 * Subscribe to the active trade pack.
 *
 * Resolves `tenant.tradePackId` from `useTenant()`. Falls back to
 * `DEFAULT_TRADE_PACK_ID` when the field is missing (preserves existing
 * Techery behaviour without requiring a tenant doc write).
 */
export function useTradePack(): UseTradePackResult {
  const { tenant } = useTenant();
  const packId = tenant?.tradePackId ?? DEFAULT_TRADE_PACK_ID;

  const [livePack, setLivePack] = useState<TradePack | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const ref = doc(db, 'trade_packs', packId);
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = { id: snapshot.id, ...snapshot.data() } as TradePack;
          setLivePack(data);
        } else {
          // Pack doc doesn't exist yet (e.g. Phase 0 pre-seed) — keep using
          // the static fallback. NOT an error condition.
          setLivePack(null);
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('[useTradePack] subscription error:', err);
        setError(err.message);
        setLivePack(null);
        setIsLoading(false);
      },
    );

    return unsubscribe;
  }, [packId]);

  // Synchronous-fallback contract: return live pack if available, otherwise
  // the static fallback. Never null.
  const pack = livePack ?? STATIC_FALLBACK_PACK;
  const isLive = livePack !== null;

  return { pack, isLoading, error, isLive };
}

// ---------------------------------------------------------------------------
// useServiceCategories — pack-driven category list with static fallback
// ---------------------------------------------------------------------------

/**
 * Returns the active pack's service categories.
 *
 * Synchronous: returns the static fallback during pack loading, swaps to
 * pack-driven categories when the subscription resolves. Stable identity
 * via useMemo so consumers can use the result in dependency arrays without
 * triggering re-renders on unrelated tenant updates.
 */
export function useServiceCategories(): ServiceCategoryDef[] {
  const { pack } = useTradePack();
  return useMemo(() => pack.serviceCategories, [pack.serviceCategories]);
}

/**
 * Lookup a single category by key.
 *
 * Returns undefined for unknown keys (legacy code paths comparing against
 * removed enum values get an undefined and should handle it).
 */
export function useServiceCategory(key: string | undefined): ServiceCategoryDef | undefined {
  const categories = useServiceCategories();
  return useMemo(
    () => (key ? categories.find((c) => c.key === key) : undefined),
    [categories, key],
  );
}

// ---------------------------------------------------------------------------
// useServiceRequestTypes — pack-driven service-request intake options
// ---------------------------------------------------------------------------

/** The static telco list, as pack-shaped data. Used when a pack declares no
 *  serviceRequestTypes of its own — which every pack did before the field
 *  existed, so this keeps old packs rendering exactly as before. */
const STATIC_FALLBACK_REQUEST_TYPES: ServiceRequestTypeDef[] = (
  Object.keys(SERVICE_REQUEST_TYPE_LABELS) as ServiceRequestType[]
).map((key) => ({
  key,
  label: SERVICE_REQUEST_TYPE_LABELS[key],
  description: SERVICE_REQUEST_TYPE_DESCRIPTIONS[key],
}));

export interface ServiceRequestTypeHelper {
  /** Types to offer when logging a new request, in pack order. */
  types: ServiceRequestTypeDef[];
  /** Label for a stored key. */
  label: (key: string | undefined) => string;
  /** Description for a stored key, or '' when unknown. */
  description: (key: string | undefined) => string;
}

/**
 * Returns the active pack's service-request types plus label/description
 * lookups.
 *
 * The lookups are deliberately more forgiving than the `types` list. A
 * service_requests doc stores its `requestType` as a plain string forever, so
 * a record logged under the telco pack still has to render after the tenant
 * moves to the electrician pack — and a bare `LABELS[key]` would render
 * `undefined` on that row. Resolution order:
 *
 *   1. the active pack's own types
 *   2. the static telco list (historical records)
 *   3. a humanised form of the key itself
 *
 * so the label is never blank and never throws.
 */
export function useServiceRequestTypes(): ServiceRequestTypeHelper {
  const { pack } = useTradePack();

  const types = useMemo(
    () =>
      pack.serviceRequestTypes && pack.serviceRequestTypes.length > 0
        ? pack.serviceRequestTypes
        : STATIC_FALLBACK_REQUEST_TYPES,
    [pack.serviceRequestTypes],
  );

  const byKey = useMemo(() => {
    const map = new Map<string, ServiceRequestTypeDef>();
    for (const t of STATIC_FALLBACK_REQUEST_TYPES) map.set(t.key, t);
    for (const t of types) map.set(t.key, t);
    return map;
  }, [types]);

  const label = useCallback(
    (key: string | undefined): string =>
      !key ? '' : byKey.get(key)?.label ?? humanizeRequestTypeKey(key),
    [byKey],
  );

  const description = useCallback(
    (key: string | undefined): string => (!key ? '' : byKey.get(key)?.description ?? ''),
    [byKey],
  );

  return { types, label, description };
}

// ---------------------------------------------------------------------------
// useTerminology — pack-driven string overrides
// ---------------------------------------------------------------------------

export interface TerminologyHelper {
  /** Resolve a terminology key, falling back to the provided default.
   *
   *  Pack overrides take precedence over tenant-level liteCustomizations
   *  overrides, which take precedence over the fallback. */
  term: (key: string, fallback: string) => string;
  /**
   * Plural form of a term: looks up `<key>_plural`, else the singular
   * override with an 's', else the provided fallback.
   *
   * Packs declare plurals explicitly rather than having them derived, because
   * naive pluralisation is wrong exactly where it matters — the electrician
   * pack's 'Sparky' pluralises to 'Sparkies', not 'Sparkys'.
   */
  termPlural: (key: string, fallback: string) => string;
  /** Raw merged terminology map (pack + tenant overrides). */
  map: Record<string, string>;
}

/**
 * Returns a terminology helper combining pack-level + tenant-level overrides.
 *
 * @example
 *   const { term } = useTerminology();
 *   <h1>{term('tech', 'Tech')}</h1>   // Renders 'Sparky' on electrician pack
 */
export function useTerminology(): TerminologyHelper {
  const { tenant } = useTenant();
  const { pack } = useTradePack();

  const merged = useMemo(() => {
    const tenantOverrides =
      tenant?.tier === 'lite' ? tenant.liteCustomizations?.customTerminology ?? {} : {};
    return { ...pack.terminology, ...tenantOverrides };
  }, [pack.terminology, tenant?.tier, tenant?.liteCustomizations?.customTerminology]);

  const term = useCallback(
    (key: string, fallback: string): string => merged[key] ?? fallback,
    [merged],
  );

  const termPlural = useCallback(
    (key: string, fallback: string): string => {
      const explicit = merged[`${key}_plural`];
      if (explicit) return explicit;
      const singular = merged[key];
      // Only derive from a singular OVERRIDE. Deriving from the caller's
      // fallback would turn 'Faults' into 'Faultss'.
      return singular ? `${singular}s` : fallback;
    },
    [merged],
  );

  return { term, termPlural, map: merged };
}

// ---------------------------------------------------------------------------
// useTradeGate — pack-driven route visibility
// ---------------------------------------------------------------------------

export interface TradeGateHelper {
  /** Pack-specific route segments to expose (tier-prefix-agnostic). */
  enabledRoutes: string[];
  /** Base route segments the pack hides. */
  hiddenRoutes: string[];
  /** True when the pack explicitly enables a pack-specific route segment. */
  isRouteEnabled: (segment: string) => boolean;
  /** True when the pack hides a base route segment.
   *  e.g. `isRouteHidden('services')` for non-telco packs. */
  isRouteHidden: (segment: string) => boolean;
}

/**
 * Pack-route convention (per docs/TRADE_PACKS.md):
 *   Pack declares: 'compliance-schedule'
 *   Pro shell mounts at: /home/compliance-schedule
 *   Lite shell mounts at: /app/compliance-schedule
 *
 * Hooks return tier-agnostic path segments. Shells decide the prefix.
 */
export function useTradeGate(): TradeGateHelper {
  const { pack } = useTradePack();

  return useMemo(() => {
    const enabledSet = new Set(pack.routes.enabled);
    const hiddenSet = new Set(pack.routes.hidden);

    return {
      enabledRoutes: pack.routes.enabled,
      hiddenRoutes: pack.routes.hidden,
      isRouteEnabled: (segment: string) => enabledSet.has(segment),
      isRouteHidden: (segment: string) => hiddenSet.has(segment),
    };
  }, [pack.routes.enabled, pack.routes.hidden]);
}

// ---------------------------------------------------------------------------
// Legacy ServiceConfig adapters (Phase 0 indirection — consumed by the 5
// React-component callers of SERVICE_CONFIGS).
//
// These return the OLD ServiceConfig shape (with field names presiteFieldDefs
// / postQuoteFieldDefs / presiteDefault / presiteCondition) so the migration
// is a pure import swap with no caller-logic changes. Reversibility: revert
// the imports in the 5 sites and they go straight back to reading
// SERVICE_CONFIGS directly.
//
// The service-layer caller (deal-workflow.service.ts) cannot use hooks and
// stays on direct SERVICE_CONFIGS imports until Phase 1+ ships pack
// hydration through services.
// ---------------------------------------------------------------------------

/**
 * Convert a pack ServiceCategoryDef back to the legacy ServiceConfig shape.
 *
 * Pack categories carry no presiteDefault / presiteCondition (those live in
 * the static fallback only). For pack-introduced categories these fields
 * default to 'never' / undefined — pack categories don't drive pre-site
 * logic, that's a Pro-tier-only legacy code path.
 */
function categoryDefToLegacyConfig(def: ServiceCategoryDef): ServiceConfig {
  const staticEntry = SERVICE_CONFIGS[def.key as keyof typeof SERVICE_CONFIGS];
  return {
    category: (staticEntry?.category ?? def.key) as ServiceConfig['category'],
    label: def.label,
    icon: def.icon,
    color: def.color,
    presiteDefault: staticEntry?.presiteDefault ?? 'never',
    presiteCondition: staticEntry?.presiteCondition,
    intakeFieldDefs: def.intakeFieldDefs,
    presiteFieldDefs: def.scopeFieldDefs,
    postQuoteFieldDefs: def.completionFieldDefs,
  };
}

/**
 * Return the active pack's categories as a `Record<string, ServiceConfig>` map
 * keyed by category key. Use this when you need to look up configs inside a
 * `.map()` without violating Rules of Hooks.
 *
 * @example
 *   const configs = useLegacyServiceConfigsMap();
 *   {selectedServices.map((svcKey) => {
 *     const config = configs[svcKey];
 *     // ...
 *   })}
 */
export function useLegacyServiceConfigsMap(): Record<string, ServiceConfig> {
  const categories = useServiceCategories();
  return useMemo(() => {
    const map: Record<string, ServiceConfig> = {};
    for (const cat of categories) {
      map[cat.key] = categoryDefToLegacyConfig(cat);
    }
    return map;
  }, [categories]);
}

/**
 * Return a single category as a legacy ServiceConfig, or undefined if not
 * present in the active pack.
 */
export function useLegacyServiceConfig(
  category: string | undefined,
): ServiceConfig | undefined {
  const map = useLegacyServiceConfigsMap();
  return category ? map[category] : undefined;
}

// ---------------------------------------------------------------------------
// useComplianceFlows — pack flows attached to a category (Phase 4 will use)
// ---------------------------------------------------------------------------

/**
 * Returns compliance flows attached to a given category key.
 *
 * Phase 0: returns empty array (no packs ship flows yet).
 * Phase 4+: returns flows from the active pack matching the category.
 */
export function useComplianceFlows(categoryKey: string | undefined): ComplianceFlow[] {
  const { pack } = useTradePack();
  return useMemo(() => {
    if (!categoryKey) return [];
    return pack.complianceFlows.filter((f) => f.appliesTo.includes(categoryKey));
  }, [pack.complianceFlows, categoryKey]);
}
